import { useMemo } from "react"

import { useTranslation } from "@bounce/i18n"

import { Region } from "@/libs/bounce/bounceSdk.types.gen"
import type { CityLink } from "@/libs/bounce/cities"
import { filterByCountry, filterByRegion, sortByAvgSearchVolume, sortByName } from "@/utils/city"

type Options = {
  cities: CityLink[]
}

export const useCities = ({ cities }: Options) => {
  const [t] = useTranslation()

  return useMemo(
    () =>
      [
        {
          id: "usa",
          label: t("page.cities.unitedStates", "United States"),
          links: cities
            .filter(city => filterByCountry(city, "US"))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
        {
          id: "canada",
          label: t("page.cities.canada", "Canada"),
          links: cities
            .filter(city => filterByCountry(city, "CA"))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
        {
          id: "mexico",
          label: t("page.cities.mexico", "Mexico"),
          links: cities
            .filter(city => filterByCountry(city, "MX"))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
        {
          id: "europe",
          label: t("page.cities.europe", "Europe"),
          links: cities
            .filter(city => filterByRegion(city, Region.Europe))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
        {
          id: "asia",
          label: t("page.cities.asia", "Asia"),
          links: cities
            .filter(city => filterByRegion(city, Region.Asia))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
        {
          id: "oceania",
          label: t("page.cities.australiaNewZealand", "Australia & New Zealand"),
          links: cities
            .filter(city => filterByRegion(city, Region.Oceania))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
        {
          id: "latinAmerica",
          label: t("page.cities.latinAmerica", "Latin America"),
          links: cities
            .filter(city => filterByRegion(city, Region.SouthAmerica))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
        {
          id: "centralAmerica",
          label: t("page.cities.centralAmerica", "Central America"),
          links: cities
            .filter(city => filterByRegion(city, Region.CentralAmerica))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
        {
          id: "middleEast",
          label: t("page.cities.middleEast", "Middle East"),
          links: cities
            .filter(city => filterByRegion(city, Region.MiddleEast))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
        {
          id: "africa",
          label: t("page.cities.africa", "Africa"),
          links: cities
            .filter(city => filterByRegion(city, Region.Africa))
            .sort(sortByAvgSearchVolume)
            .sort(sortByName),
        },
      ].filter(section => section.links.length > 0),
    [cities, t],
  )
}
